import React, { useCallback, useEffect, useState } from "react";
import { Inbox, Loader2, RefreshCw, Save, CalendarDays, MessageSquare, Newspaper, DoorOpen, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { admin } from "@/lib/api";
import { cn } from "@/lib/utils";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
const STATUSES = ["new", "contacted", "qualified", "won", "lost"];
const STATUS_TONE = {
  new: "text-mk-lime border-mk-lime/40 bg-mk-lime/10", contacted: "text-[#7fb0ff] border-mk-blue/40 bg-mk-blue/10",
  qualified: "text-amber-300 border-amber-400/40 bg-amber-400/10", won: "text-emerald-300 border-emerald-400/40 bg-emerald-400/10",
  lost: "text-mk-muted border-mk-border bg-mk-bg",
};
const TYPE_META = {
  demo: { label: "Demo request", icon: CalendarDays, tone: "text-mk-lime" },
  contact: { label: "Enquiry", icon: MessageSquare, tone: "text-[#7fb0ff]" },
  newsletter: { label: "Newsletter", icon: Newspaper, tone: "text-amber-300" },
  exit: { label: "Exit-intent", icon: DoorOpen, tone: "text-pink-300" },
  chat: { label: "Chat transcript", icon: Sparkles, tone: "text-emerald-300" },
};

const Stat = ({ label, value, hint, testId }) => (
  <div className="rounded-2xl border border-mk-border bg-mk-surface p-5" data-testid={testId}>
    <div className="text-xs uppercase tracking-wider text-mk-muted font-mono">{label}</div>
    <div className="mt-2 font-display text-3xl font-bold text-mk-text tabular-nums">{value}</div>
    {hint && <div className="mt-1 text-xs text-mk-dim">{hint}</div>}
  </div>
);

const LeadRow = ({ lead, onSaved }) => {
  const [note, setNote] = useState(lead.note || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setNote(lead.note || ""); }, [lead.note]);
  const key = `${lead.type}-${lead.id}`;
  const dirty = note !== (lead.note || "");
  const T = TYPE_META[lead.type];
  const save = async (payload, msg) => {
    setBusy(true);
    try { onSaved(await admin.updateLead(lead.type, lead.id, payload)); toast.success(msg); }
    catch (err) { toast.error(err?.response?.data?.detail || "Couldn't save"); }
    finally { setBusy(false); }
  };
  return (
    <tr className="border-b border-mk-border/60 hover:bg-mk-raised/30 align-top" data-testid={`lead-row-${key}`}>
      <td className="px-5 py-3 whitespace-nowrap">
        <span className={cn("inline-flex items-center gap-1.5 text-xs font-mono", T.tone)} data-testid={`lead-type-${key}`}><T.icon className="h-3.5 w-3.5" />{T.label}</span>
        <div className="mt-1 text-xs text-mk-muted">{fmt(lead.created_at)}{lead.hits > 1 && ` · ${lead.hits} hits`}</div>
      </td>
      <td className="px-3 py-3">
        <div className="font-medium text-mk-text">{lead.name || "—"}{lead.company && <span className="text-mk-dim font-normal"> · {lead.company}</span>}</div>
        <a href={`mailto:${lead.email}`} className="text-xs text-mk-dim hover:text-mk-lime" data-testid={`lead-email-${key}`}>{lead.email}</a>
      </td>
      <td className="px-3 py-3 max-w-md">
        <div className="text-mk-text">{lead.headline}</div>
        {lead.detail && <div className="text-xs text-mk-dim">{lead.detail}</div>}
        {lead.message && <p className="mt-1 text-xs text-mk-dim whitespace-pre-wrap line-clamp-4">{lead.message}</p>}
      </td>
      <td className="px-3 py-3">
        <select value={lead.status} disabled={busy} onChange={(e) => save({ status: e.target.value }, `Marked ${e.target.value}`)} data-testid={`lead-status-${key}`}
          className={cn("rounded-full border px-3 py-1 text-xs capitalize focus:outline-none focus:ring-2 focus:ring-mk-lime/20 cursor-pointer", STATUS_TONE[lead.status])}>
          {STATUSES.map((s) => <option key={s} value={s} className="bg-mk-bg text-mk-text">{s}</option>)}
        </select>
        {lead.status_updated_at && <div className="mt-1 text-[10px] text-mk-muted whitespace-nowrap">{fmt(lead.status_updated_at)}</div>}
      </td>
      <td className="px-5 py-3 min-w-[240px]">
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Internal note…" className="input-dark-sm resize-y" data-testid={`lead-note-${key}`} />
        <div className="mt-1 flex items-center justify-between gap-2 text-[10px] text-mk-muted">
          <span>{lead.note_updated_at ? `saved ${fmt(lead.note_updated_at)}` : ""}</span>
          {dirty && <button type="button" onClick={() => save({ note }, "Note saved")} disabled={busy} className="btn-ghost !py-0.5 !px-2 text-[11px]" data-testid={`lead-note-save-${key}`}>{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Save className="h-3 w-3" />Save note</>}</button>}
        </div>
      </td>
    </tr>
  );
};

const countBy = (items, field) => items.reduce((acc, i) => ({ ...acc, [i[field]]: (acc[i[field]] || 0) + 1 }), {});

export default function LeadInbox({ onCounts }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => { const d = await admin.leads(); setData(d); onCounts?.(d.counts); }, [onCounts]);
  useEffect(() => { load().catch(() => toast.error("Couldn't load leads")); }, [load]);

  const onSaved = (updated) => setData((d) => {
    const items = d.items.map((i) => (i.type === updated.type && i.id === updated.id ? { ...i, ...updated } : i));
    const counts = { ...d.counts, status: countBy(items, "status") };
    onCounts?.(counts);
    return { ...d, items, counts };
  });

  const t = data?.counts?.type || {};
  return (
    <div className="space-y-6" data-testid="lead-inbox">
      <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-4">
        <Stat label="New" value={data?.counts?.status?.new ?? "—"} hint="awaiting first touch" testId="lead-stat-new" />
        <Stat label="Demo requests" value={t.demo ?? 0} testId="lead-stat-demo" />
        <Stat label="Enquiries" value={t.contact ?? 0} testId="lead-stat-contact" />
        <Stat label="Newsletter" value={t.newsletter ?? 0} testId="lead-stat-newsletter" />
        <Stat label="Exit-intent" value={t.exit ?? 0} hint="from the pricing page" testId="lead-stat-exit" />
        <Stat label="Chat transcripts" value={t.chat ?? 0} hint="emailed from the assistant" testId="lead-stat-chat" />
      </div>

      <div className="rounded-2xl border border-mk-border bg-mk-surface overflow-hidden" data-testid="lead-table">
        <div className="flex flex-wrap items-center gap-3 border-b border-mk-border px-5 py-3">
          <Inbox className="h-4 w-4 text-mk-lime" /><div className="font-display font-semibold text-mk-text">Lead inbox</div>
          <span className="text-xs text-mk-muted">{data ? `${data.count} total · newest first` : ""}</span>
          <button type="button" onClick={() => load().catch(() => toast.error("Couldn't refresh"))} className="ml-auto btn-ghost !py-1.5 !px-3 text-xs" data-testid="lead-refresh"><RefreshCw className="h-3.5 w-3.5" />Refresh</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wider text-mk-muted font-mono">
              <tr className="border-b border-mk-border"><th className="px-5 py-3">Source</th><th className="px-3 py-3">Who</th><th className="px-3 py-3">What</th><th className="px-3 py-3">Status</th><th className="px-5 py-3">Note</th></tr>
            </thead>
            <tbody>
              {data === null && <tr><td colSpan={5} className="px-5 py-8 text-center text-mk-muted"><Loader2 className="inline h-4 w-4 animate-spin" /></td></tr>}
              {data?.items.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-mk-muted" data-testid="lead-empty">No leads yet — demo requests, enquiries, newsletter signups and exit-intent captures land here.</td></tr>}
              {data?.items.map((l) => <LeadRow key={`${l.type}-${l.id}`} lead={l} onSaved={onSaved} />)}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
